import { motion, AnimatePresence } from 'framer-motion';
import { X, Server, Shield, CheckCircle, Target, Award } from 'lucide-react';

interface CollocationModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export default function CollocationModal({ isOpen, onClose }: CollocationModalProps) {
  if (!isOpen) return null;

  return (
    <AnimatePresence>
      {isOpen && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4 overflow-y-auto">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.95 }}
            className="bg-gradient-to-br from-slate-900 via-indigo-900 to-slate-900 rounded-2xl max-w-5xl w-full my-8 border border-indigo-500/30 shadow-2xl shadow-indigo-500/20"
          >
            {/* Header */}
            <div className="relative p-8 border-b border-indigo-500/30">
              <div className="flex items-start justify-between">
                <div className="flex items-center gap-4">
                  <div className="p-3 bg-gradient-to-br from-indigo-500 to-purple-500 rounded-xl">
                    <Server className="w-10 h-10 text-white" />
                  </div>
                  <div>
                    <h2 className="text-4xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-indigo-400 to-purple-400">
                      Collocation - Serveurs Dédiés
                    </h2>
                    <p className="text-gray-400 mt-1 text-lg">
                      Contrôle total avec infrastructure BZ - Standards de qualité de salle
                    </p>
                  </div>
                </div>
                <button
                  onClick={onClose}
                  className="p-2 hover:bg-white/10 rounded-lg transition-colors"
                >
                  <X className="w-6 h-6 text-gray-400" />
                </button>
              </div>
            </div>

            {/* Content */}
            <div className="p-8 space-y-8 max-h-[calc(100vh-300px)] overflow-y-auto">
              
              <div className="bg-gradient-to-r from-indigo-600/20 via-purple-600/20 to-indigo-600/20 rounded-xl p-8 border-2 border-indigo-500/50">
                <h3 className="text-2xl font-bold text-center text-white mb-4">
                  Collocation : Contrôle Total
                </h3>
                <p className="text-gray-300 text-center text-lg">
                  La collocation est pour les clients qui veulent le <strong className="text-white">contrôle total</strong> sur leurs serveurs. 
                  Vous avez vos <strong className="text-indigo-400">serveurs dédiés</strong>, vous gérez votre <strong className="text-purple-400">hypervisor</strong> et vos 
                  <strong className="text-purple-400"> VM</strong>. Nous, nous fournissons le <strong className="text-white">matériel</strong> et 
                  l'<strong className="text-white">infrastructure physique</strong> avec nos <strong className="text-white">standards de qualité de salle</strong>.
                </p>
              </div>

              <div className="space-y-6">
                <div className="bg-gradient-to-br from-indigo-500/10 to-purple-500/10 rounded-xl p-6 border border-indigo-500/30">
                  <h4 className="text-2xl font-bold text-white mb-4 flex items-center gap-2">
                    <Target className="w-7 h-7 text-indigo-400" />
                    Responsabilités Client
                  </h4>
                  <p className="text-gray-300 mb-4">
                    En collocation, vous avez la responsabilité complète du contenu de vos serveurs :
                  </p>
                  <ul className="space-y-2 text-gray-300">
                    <li className="flex items-start gap-2">
                      <CheckCircle className="w-5 h-5 text-indigo-400 flex-shrink-0 mt-0.5" />
                      <span><strong className="text-white">Hypervisor</strong> - Gestion complète de votre hyperviseur</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle className="w-5 h-5 text-indigo-400 flex-shrink-0 mt-0.5" />
                      <span><strong className="text-white">Machines Virtuelles (VM)</strong> - Création, gestion, maintenance</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle className="w-5 h-5 text-indigo-400 flex-shrink-0 mt-0.5" />
                      <span><strong className="text-white">Contenu des serveurs</strong> - Applications, données, configurations</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle className="w-5 h-5 text-indigo-400 flex-shrink-0 mt-0.5" />
                      <span><strong className="text-white">Sécurité logicielle</strong> - Protection, mises à jour, monitoring</span>
                    </li>
                  </ul>
                </div>

                <div className="bg-gradient-to-br from-purple-500/10 to-pink-500/10 rounded-xl p-6 border border-purple-500/30">
                  <h4 className="text-2xl font-bold text-white mb-4 flex items-center gap-2">
                    <Award className="w-7 h-7 text-purple-400" />
                    Ce Que BZ Fournit
                  </h4>
                  <p className="text-gray-300 mb-4">
                    BZ Inc. fournit le matériel et l'infrastructure physique avec nos standards de qualité de salle :
                  </p>
                  <ul className="space-y-2 text-gray-300">
                    <li className="flex items-start gap-2">
                      <CheckCircle className="w-5 h-5 text-purple-400 flex-shrink-0 mt-0.5" />
                      <span><strong className="text-white">Matériel serveur</strong> - Serveurs physiques dédiés</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle className="w-5 h-5 text-purple-400 flex-shrink-0 mt-0.5" />
                      <span><strong className="text-white">Infrastructure réseau</strong> - Connexions, switches, routage</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle className="w-5 h-5 text-purple-400 flex-shrink-0 mt-0.5" />
                      <span><strong className="text-white">Redondance génératrice N+1</strong> - Alimentation de secours</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle className="w-5 h-5 text-purple-400 flex-shrink-0 mt-0.5" />
                      <span><strong className="text-white">UPS N+1</strong> - Alimentation sans interruption</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle className="w-5 h-5 text-purple-400 flex-shrink-0 mt-0.5" />
                      <span><strong className="text-white">Climatisation</strong> - Contrôle de température et humidité</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle className="w-5 h-5 text-purple-400 flex-shrink-0 mt-0.5" />
                      <span><strong className="text-white">Sécurité physique</strong> - Contrôles d'accès, monitoring</span>
                    </li>
                  </ul>
                </div>

                <div className="bg-gradient-to-br from-blue-500/10 to-cyan-500/10 rounded-xl p-6 border border-blue-500/30">
                  <h4 className="text-2xl font-bold text-white mb-4 flex items-center gap-2">
                    <Shield className="w-7 h-7 text-blue-400" />
                    Standards de Qualité BZ
                  </h4>
                  <p className="text-gray-300">
                    Nos standards de qualité de salle garantissent une infrastructure <strong className="text-white">robuste</strong>, 
                    <strong className="text-white"> redondante</strong>, et <strong className="text-white">fiable</strong>. 
                    Vous vous concentrez sur votre infrastructure virtuelle, nous gérons l'infrastructure physique avec excellence.
                  </p>
                </div>
              </div>

            </div>

            {/* Footer */}
            <div className="p-6 border-t border-indigo-500/30 bg-slate-900/50">
              <div className="flex items-center justify-end">
                <button
                  onClick={onClose}
                  className="px-6 py-3 bg-gradient-to-r from-indigo-500 to-purple-500 text-white rounded-xl hover:from-indigo-600 hover:to-purple-600 transition-all duration-200 font-semibold shadow-lg shadow-indigo-500/20 hover:shadow-indigo-500/40 hover:scale-105"
                >
                  Fermer
                </button>
              </div>
            </div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
}












